import { Fragment, useEffect, useState } from "react"

import { Collapse, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Button from "../../../Components/Button"
import ImageWithPreview from "../../../Components/ImageWithPreview"
import Modal from "../../../Components/Modal"
import Icon from "../../../Icon"
import { MCP_CARDS, MCP_GUIDES } from "./guides"

const { Text, Link } = Typography
const { Panel } = Collapse

// Instruction copy carries **bold** spans (the design's <strong>) and `inline code`
// spans (the design's <code> chips, used for commands / file names / keystrokes).
// Translate the whole sentence, then render the bold and code runs accordingly.
const renderRich = (text, t) =>
  t(text, { keySeparator: false })
    .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
    .map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>
      }

      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code className="mcp-inline-code" key={i}>
            {part.slice(1, -1)}
          </code>
        )
      }

      return <Fragment key={i}>{part}</Fragment>
    })

const CALLOUT_ICON = { info: "circle_info", success: "circle_info", warning: "circle_info" }

const Block = ({ block, t }) => {
  switch (block.type) {
    case "text":
      return (
        <div
          className={`mcp-step-text${block.weight === "regular" ? " mcp-step-text--regular" : ""}`}
        >
          {renderRich(block.text, t)}
        </div>
      )
    case "callout":
      return (
        <div className={`mcp-callout mcp-callout-${block.variant}`}>
          <Icon type={CALLOUT_ICON[block.variant] || "circle_info"} className="mcp-callout-icon" />
          <div className="mcp-callout-text">{renderRich(block.text, t)}</div>
        </div>
      )
    case "code":
      return (
        <>
          {block.label ? <div className="mcp-code-label">{t(block.label)}</div> : null}
          <div className="mcp-code">
            <code>{block.code}</code>
            <Text className="mcp-code-copy" copyable={{ text: block.code, tooltips: false }} />
          </div>
        </>
      )
    case "fields":
      return (
        <div className="mcp-fields">
          {block.rows.map((row, i) => (
            <div className="mcp-field" key={i}>
              <span className="mcp-field-label">{t(row.label)}</span>
              {row.code ? (
                <code className="mcp-inline-code">{row.code}</code>
              ) : (
                <span className="mcp-field-value">{renderRich(row.value, t)}</span>
              )}
            </div>
          ))}
        </div>
      )
    case "spacer":
      return (
        <div
          className="mcp-spacer"
          style={block.size != null ? { height: block.size } : undefined}
        />
      )
    case "note":
      return <div className="mcp-step-note">{renderRich(block.text, t)}</div>
    case "image":
      return (
        <div className="mcp-shot">
          <ImageWithPreview src={block.src} alt="" width="100%" />
          <div className="mcp-shot-caption">
            <Icon type="full_screen" />
            {t("Click on the image to open it full screen.")}
          </div>
        </div>
      )
    default:
      return null
  }
}

// One numbered step list. Reused for plain guides and for each OS tab of a
// guide that ships per-platform variants (e.g. Gemini CLI: Mac / Linux / Windows).
const Steps = ({ steps, t }) => (
  <div className="mcp-steps">
    {steps.map((step, i) => (
      <div className="mcp-step" key={i}>
        <div className="mcp-step-rail">
          <span className="mcp-step-num">{i + 1}</span>
          {i < steps.length - 1 ? <span className="mcp-step-line" /> : null}
        </div>
        <div className="mcp-step-body">
          {step.blocks.map((block, j) => (
            <Block key={j} block={block} t={t} />
          ))}
        </div>
      </div>
    ))}
  </div>
)

const McpGuideModal = ({ guideKey, onClose }) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const theme = useSelector((state) => state.nav.theme)
  const guide = guideKey ? MCP_GUIDES[guideKey] : null

  // OS-variant tab (only used when the guide defines `variants`). Reset to the
  // first tab whenever a different guide opens.
  const [tab, setTab] = useState(0)

  useEffect(() => {
    setTab(0)
  }, [guideKey])

  // Header icon mirrors the integration's card icon (cards map to guides via `guide`).
  const card = guideKey ? MCP_CARDS.find((c) => c.guide === guideKey) : null

  const title = guide ? (
    <div className="mcp-modal-head">
      {card?.icon ? (
        <span className="mcp-modal-icon">
          <img src={theme === "dark" && card.icon_dark ? card.icon_dark : card.icon} alt="" />
        </span>
      ) : null}
      <div>
        <div className="mcp-modal-title">{t(guide.title)}</div>
        <div className="mcp-modal-sub">{t(guide.subtitle)}</div>
      </div>
    </div>
  ) : (
    ""
  )

  const footer = guide?.footer ? (
    <Flex
      align={isMobile ? "stretch" : "center"}
      justify="space-between"
      vertical={isMobile}
      style={{ width: "100%" }}
      gap={isMobile ? 12 : 0}
    >
      <Text type="secondary" className="mcp-foot-note">
        <Icon type="circle_info" />
        {t(guide.footer.note)}
      </Text>
      <Link
        href={guide.footer.href}
        target="_blank"
        rel="noopener noreferrer"
        className="mcp-footer-link"
      >
        <Button
          type="primary"
          className="flex center"
          text={t(guide.footer.btn)}
          icon={<Icon type="open_link" />}
        />
      </Link>
    </Flex>
  ) : null

  return (
    <Modal
      visible={!!guide}
      handleClose={onClose}
      title={title}
      buttons={footer}
      width={isMobile ? "100%" : 560}
      isMobile={isMobile}
      destroyOnClose
      className="mcp-guide-modal"
    >
      {guide && (
        <>
          {guide.intro?.length ? (
            <div className="mcp-intro">
              {guide.intro.map((block, i) => (
                <Block key={i} block={block} t={t} />
              ))}
            </div>
          ) : null}

          {guide.variants?.length ? (
            <>
              <div className="mcp-tabs" role="tablist">
                {guide.variants.map((v, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === tab}
                    className={`mcp-tab${i === tab ? " active" : ""}`}
                    onClick={() => setTab(i)}
                  >
                    {t(v.label)}
                  </button>
                ))}
              </div>
              <Steps steps={(guide.variants[tab] || guide.variants[0]).steps} t={t} />
            </>
          ) : (
            <Steps steps={guide.steps} t={t} />
          )}

          {guide.disconnect ? (
            <Collapse
              ghost
              className="mcp-disconnect"
              expandIconPosition="end"
              expandIcon={({ isActive }) => (
                <Icon
                  type="chevron_down_small"
                  className="mcp-disconnect-arrow"
                  style={{ transform: isActive ? "rotate(180deg)" : "none" }}
                />
              )}
            >
              <Panel
                key="disconnect"
                header={
                  <div className="mcp-disconnect-head">
                    <span className="mcp-disconnect-title">{t("How to disconnect")}</span>
                    <span className="mcp-disconnect-sub">
                      {t(guide.disconnect.subtitle || "Remove access to Chargeback")}
                    </span>
                  </div>
                }
              >
                {guide.disconnect.blocks?.length ? (
                  <div className="mcp-disconnect-blocks">
                    {guide.disconnect.blocks.map((block, i) => (
                      <Block key={i} block={block} t={t} />
                    ))}
                  </div>
                ) : null}
                {guide.disconnect.steps?.length ? (
                  <ol className="mcp-disconnect-steps">
                    {guide.disconnect.steps.map((s, i) => (
                      <li key={i}>{renderRich(s, t)}</li>
                    ))}
                  </ol>
                ) : null}
                {guide.disconnect.image ? (
                  <div className="mcp-shot">
                    <ImageWithPreview src={guide.disconnect.image} alt="" width="100%" />
                    <div className="mcp-shot-caption">
                      <Icon type="open_link" />
                      {t("Click on the image to open it full screen.")}
                    </div>
                  </div>
                ) : null}
                {guide.disconnect.note ? (
                  <Text type="secondary" className="mcp-disconnect-note">
                    {renderRich(guide.disconnect.note, t)}
                  </Text>
                ) : null}
              </Panel>
            </Collapse>
          ) : null}
        </>
      )}
    </Modal>
  )
}

export default McpGuideModal
